/**
 * Best-effort DOM-to-PNG for the reward certificate.
 *
 * We can't ship html2canvas (too heavy, +30 KB gz) just for one share
 * flow. Instead we take the classic "SVG foreignObject" trick:
 *   1. Wrap the certificate's outerHTML in an SVG <foreignObject>.
 *   2. Rasterise via canvas + a temporary <img>.
 *   3. Return a File the caller can hand to navigator.share({files}).
 *
 * If ANY step fails (Safari sometimes refuses foreignObject with
 * embedded stylesheets), we return null and the caller falls back to
 * text-only share — WhatsApp will still show the link preview.
 *
 * A note on styling: because we embed the certificate DOM as-is, most
 * inline styles work but @-rules and external fonts may render as
 * fallbacks. Kids won't mind — the image is a nice-to-have preview.
 */

const WIDTH = 720;

export async function tryCertificateFile(certRef, filename = "certificate.png") {
  try {
    if (!certRef || !certRef.current) return null;
    const el = certRef.current;
    const rect = el.getBoundingClientRect();
    const width  = Math.min(WIDTH, Math.ceil(rect.width || WIDTH));
    const height = Math.ceil(rect.height || WIDTH);

    // Clone so any input focus / hover state doesn't leak into the image.
    const clone = el.cloneNode(true);
    // Force any print-hidden node to stay hidden in the image too.
    clone.querySelectorAll("[data-print-hidden='true'], .print\\:hidden").forEach((n) => {
      n.style.display = "none";
    });

    const xml = new XMLSerializer().serializeToString(clone);
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">` +
      `<foreignObject width="100%" height="100%">` +
      `<div xmlns="http://www.w3.org/1999/xhtml" style="width:${width}px;background:#ffffff;">` +
      xml +
      `</div>` +
      `</foreignObject>` +
      `</svg>`;

    const svgBlob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.crossOrigin = "anonymous";

    const loaded = new Promise((resolve, reject) => {
      img.onload  = () => resolve();
      img.onerror = () => reject(new Error("image-load-failed"));
    });
    img.src = url;
    await loaded;

    const canvas = document.createElement("canvas");
    canvas.width  = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);
    URL.revokeObjectURL(url);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 0.92));
    if (!blob) return null;
    const file = new File([blob], filename, { type: "image/png" });
    // Some browsers (Safari desktop) tainted-canvas here — canShare will
    // return false and we bail cleanly.
    return [file];
  } catch {
    return null;
  }
}
